import React, { useContext, useState } from "react";
import {
  Box, Typography, TextField, Button, Alert,
  CircularProgress, Stack, IconButton, Checkbox, FormControlLabel,
} from "@mui/material";
import { Person, LocalHospital,
         CheckCircleOutline, EmailOutlined, PhoneIphone, MarkEmailRead,
         ArrowBack } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import UserContext from "./UserContext";
import ThemeToggle from "./ThemeToggle";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const BRAND = {
  navy: "#0a0e27",
  navyDark: "#0f1233",
  ink: "#0f1233",
  slate: "#5b6584",
  accent: "#637cdb",
  accentDark: "#3F6AC0",
  lightBg: "#eef1f8",
  border: "#e2e6f0",
};

// ── Cash-pay test panels — optional, patient-only, multi-select ─────────────
const PANEL_OPTIONS = [
  {
    key: "upload_interpretation",
    label: "$99 — Upload My Existing Test Result",
    desc: "Upload your old Proove report, or a PGX/drug-metabolism report from another lab, to get your Proove AI-powered interpretation. (Opioid Risk and Pain Perception are Proove exclusives — only Proove's own reports can be analyzed for those two.)",
  },
  {
    key: "drug_metabolism",
    label: "$599 — Proove Drug Metabolism",
    desc: "Cash Pay (no insurance bill).",
  },
  {
    key: "opioid_risk",
    label: "$299 — Proove Opioid Risk",
    desc: "Proove exclusive test. Cash Pay (no insurance bill).",
  },
  {
    key: "pain_perception",
    label: "$199 — Proove Pain Perception",
    desc: "Proove exclusive test. Cash Pay (no insurance bill).",
  },
  {
    key: "all_panels",
    label: "$799 — All 3 Proove Panels",
    desc: "Drug Metabolism, Opioid Risk, and Pain Perception. Cash Pay (no insurance bill).",
  },
];

const PANEL_AMOUNTS = {
  upload_interpretation: 99,
  drug_metabolism: 599,
  opioid_risk: 299,
  pain_perception: 199,
  all_panels: 799,
};

function Field({ label, required, hint, isDark, children }) {
  const sub = isDark ? "rgba(255,255,255,0.55)" : "#475569";
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: sub, textTransform: "uppercase" }}>
          {label}
          {required && <Box component="span" sx={{ color: BRAND.accent, ml: 0.4 }}>*</Box>}
        </Typography>
        {hint && <Typography sx={{ fontSize: 12.5, color: sub }}>{hint}</Typography>}
      </Box>
      {children}
    </Box>
  );
}

function RoleSelector({ role, onChange, isDark }) {
  const cards = [
    { value: "patient",   label: "Patient",             sub: "Plain-language lab result context",         Icon: Person },
    { value: "clinician", label: "Clinician / Provider", sub: "Evidence-based clinical decision support", Icon: LocalHospital },
  ];
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em",
        color: isDark ? "rgba(255,255,255,0.55)" : "#475569", textTransform: "uppercase", mb: 1.25 }}>
        I AM A…
      </Typography>
      <Stack direction="row" spacing={1.5}>
        {cards.map(({ value, label, sub, Icon }) => {
          const active = role === value;
          return (
            <Box key={value} onClick={() => onChange(value)} sx={{
              flex: 1, cursor: "pointer", borderRadius: "10px", p: 1.75,
              border: `2px solid ${active ? BRAND.navy : (isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.14)")}`,
              bgcolor: active
                ? (isDark ? "rgba(10,14,39,0.2)" : "rgba(10,14,39,0.04)")
                : (isDark ? "rgba(255,255,255,0.03)" : "rgba(10,14,39,0.018)"),
              boxShadow: active ? "0 0 0 3px rgba(10,14,39,0.08)" : "none",
              transition: "all 0.2s",
              "&:hover": { borderColor: active ? BRAND.navy : (isDark ? "rgba(255,255,255,0.22)" : "rgba(10,14,39,0.3)") },
            }}>
              <Icon sx={{ fontSize: 20, color: active ? BRAND.navy : (isDark ? "#64748b" : "#94a3b8"), mb: 0.5 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2, mb: 0.3,
                color: active ? (isDark ? "#f1f5f9" : BRAND.navy) : (isDark ? "#94a3b8" : "#475569") }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: 11, color: isDark ? "#475569" : "#94a3b8", lineHeight: 1.4 }}>
                {sub}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

// ── Payment-sent confirmation screen ─────────────────────────────────────────
function PaymentSentScreen({ email, phone, isDark, selectedItems }) {
  const navigate = useNavigate();
  const cardBg   = isDark ? "rgba(17,24,39,0.62)" : "rgba(255,255,255,0.68)";
  const textSub  = isDark ? "#94a3b8" : "#64748b";

  const items = selectedItems && selectedItems.length
    ? selectedItems
    : [{ label: "Proove Lab Test Co-pay", amount: 25 }];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
    >
      <Box sx={{
        width: { xs: "100%", sm: 480 }, maxWidth: 480,
        borderRadius: 3, overflow: "hidden",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(10,14,39,0.12)"}`,
        boxShadow: isDark
          ? "0 24px 80px rgba(0,0,0,0.5), 0 0 50px -18px rgba(99,124,219,0.45)"
          : "0 24px 80px rgba(10,14,39,0.12), 0 0 40px -20px rgba(99,124,219,0.3)",
        bgcolor: cardBg,
        backdropFilter: "blur(20px) saturate(140%)",
        WebkitBackdropFilter: "blur(20px) saturate(140%)",
      }}>
        <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.accent})` }} />

        {/* Header */}
        <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3.5, pb: 3.5, textAlign: "center", position: "relative", overflow: "hidden" }}>
          <Box sx={{ position: "absolute", top: -40, right: -40, width: 140, height: 140,
            borderRadius: "50%", border: "1px solid rgba(99,124,219,0.15)" }} />
          <Box sx={{ position: "absolute", top: -20, right: -20, width: 90, height: 90,
            borderRadius: "50%", border: "1px solid rgba(99,124,219,0.2)" }} />

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 18 }}
          >
            <CheckCircleOutline sx={{ fontSize: 56, color: "#22c55e", mb: 1.5 }} />
          </motion.div>
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.15em", color: BRAND.accent, mb: 0.75 }}>
            ACCOUNT CREATED
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>
            Check Your Email & Phone
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ px: 4, py: 3.5 }}>
          <Typography sx={{ fontSize: 16, color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.8, mb: 2, textAlign: "center" }}>
            Your login credentials and payment link(s) for the following have been sent to:
          </Typography>

          {/* Selected items summary */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mb: 3 }}>
            {items.map((item, i) => (
              <Box key={i} sx={{
                fontSize: 12.5, fontWeight: 700,
                color: isDark ? "#e2e8f0" : BRAND.navy,
                bgcolor: isDark ? "rgba(99,124,219,0.15)" : "rgba(99,124,219,0.1)",
                border: `1px solid ${isDark ? "rgba(99,124,219,0.3)" : "rgba(99,124,219,0.25)"}`,
                borderRadius: "99px", px: 1.75, py: 0.6,
              }}>
                {item.label} (${item.amount})
              </Box>
            ))}
          </Box>

          {/* Delivery channels */}
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1.5,
              bgcolor: isDark ? "rgba(10,14,39,0.15)" : "rgba(10,14,39,0.04)",
              border: `1px solid ${isDark ? "rgba(10,14,39,0.3)" : "rgba(10,14,39,0.12)"}`,
              borderRadius: "10px", px: 2, py: 1.25,
            }}>
              <EmailOutlined sx={{ fontSize: 20, color: BRAND.navy, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 14.5, color: isDark ? "#e2e8f0" : "#1e293b", fontWeight: 600 }}>
                {email}
              </Typography>
            </Box>
            {phone && (
              <Box sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                bgcolor: isDark ? "rgba(10,14,39,0.15)" : "rgba(10,14,39,0.04)",
                border: `1px solid ${isDark ? "rgba(10,14,39,0.3)" : "rgba(10,14,39,0.12)"}`,
                borderRadius: "10px", px: 2, py: 1.25,
              }}>
                <PhoneIphone sx={{ fontSize: 20, color: BRAND.navy, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 14.5, color: isDark ? "#e2e8f0" : "#1e293b", fontWeight: 600 }}>
                  {phone}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Steps */}
          <Box sx={{
            bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8f6f2",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(10,14,39,0.08)"}`,
            borderRadius: "10px", px: 2.5, py: 2, mb: 3,
          }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: isDark ? "#94a3b8" : "#64748b",
              letterSpacing: "0.1em", textTransform: "uppercase", mb: 1.25 }}>
              What to do next
            </Typography>
            {[
              "Check your email for your temporary login credentials",
              "Sign in at proove.com — you'll be prompted to set a new password",
              "Open the payment link(s) (also in your email & text) to complete your selected items",
              "A payment confirmation will be sent once each payment is received",
            ].map((step, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: i < 3 ? 1.25 : 0 }}>
                <Box sx={{
                  width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                  bgcolor: BRAND.navy, color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 800, mt: 0.1,
                }}>
                  {i + 1}
                </Box>
                <Typography sx={{ fontSize: 14, color: isDark ? "#cbd5e1" : "#475569", lineHeight: 1.65 }}>
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography sx={{ fontSize: 13.5, color: textSub, textAlign: "center", lineHeight: 1.7, mb: 2.5 }}>
            One-time payment(s) — no subscriptions. Don't see the email?
            Check your spam folder or{" "}
            <Box component="a" href="mailto:customerservice@proove.com"
              sx={{ color: isDark ? BRAND.accent : BRAND.navy, fontWeight: 700, textDecoration: "underline" }}>
              contact us
            </Box>
          </Typography>

          <Button
            fullWidth variant="outlined"
            onClick={() => navigate("/")}
            sx={{
              borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(10,14,39,0.2)",
              color: isDark ? "#94a3b8" : "#64748b",
              borderRadius: "10px", py: 1.3, fontWeight: 600, textTransform: "none", fontSize: 14,
              "&:hover": { borderColor: BRAND.navy, color: BRAND.navy, bgcolor: "transparent" },
            }}
          >
            Go to Sign In
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
}

// ── Clinician account-created confirmation screen ─────────────────────────────
function ClinicianAccountCreatedScreen({ email, isDark }) {
  const navigate = useNavigate();
  const cardBg   = isDark ? "rgba(17,24,39,0.62)" : "rgba(255,255,255,0.68)";
  const textSub  = isDark ? "#94a3b8" : "#64748b";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
    >
      <Box sx={{
        width: { xs: "100%", sm: 480 }, maxWidth: 480,
        borderRadius: 3, overflow: "hidden",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(10,14,39,0.12)"}`,
        boxShadow: isDark
          ? "0 24px 80px rgba(0,0,0,0.5), 0 0 50px -18px rgba(99,124,219,0.45)"
          : "0 24px 80px rgba(10,14,39,0.12), 0 0 40px -20px rgba(99,124,219,0.3)",
        bgcolor: cardBg,
        backdropFilter: "blur(20px) saturate(140%)",
        WebkitBackdropFilter: "blur(20px) saturate(140%)",
      }}>
        <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.accent})` }} />

        {/* Header */}
        <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3.5, pb: 3.5, textAlign: "center", position: "relative", overflow: "hidden" }}>
          <Box sx={{ position: "absolute", top: -40, right: -40, width: 140, height: 140,
            borderRadius: "50%", border: "1px solid rgba(99,124,219,0.15)" }} />
          <Box sx={{ position: "absolute", top: -20, right: -20, width: 90, height: 90,
            borderRadius: "50%", border: "1px solid rgba(99,124,219,0.2)" }} />

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 18 }}
          >
            <MarkEmailRead sx={{ fontSize: 56, color: "#22c55e", mb: 1.5 }} />
          </motion.div>
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.15em", color: BRAND.accent, mb: 0.75 }}>
            ACCOUNT CREATED
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>
            Check Your Email
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ px: 4, py: 3.5 }}>
          <Typography sx={{ fontSize: 16, color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.8, mb: 3, textAlign: "center" }}>
            Your clinician account has been created. Your login credentials and a
            temporary password have been sent to:
          </Typography>

          <Box sx={{
            display: "flex", alignItems: "center", gap: 1.5, mb: 3,
            bgcolor: isDark ? "rgba(10,14,39,0.15)" : "rgba(10,14,39,0.04)",
            border: `1px solid ${isDark ? "rgba(10,14,39,0.3)" : "rgba(10,14,39,0.12)"}`,
            borderRadius: "10px", px: 2, py: 1.25,
          }}>
            <EmailOutlined sx={{ fontSize: 20, color: BRAND.navy, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 14.5, color: isDark ? "#e2e8f0" : "#1e293b", fontWeight: 600 }}>
              {email}
            </Typography>
          </Box>

          {/* Steps */}
          <Box sx={{
            bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8f6f2",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(10,14,39,0.08)"}`,
            borderRadius: "10px", px: 2.5, py: 2, mb: 3,
          }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: isDark ? "#94a3b8" : "#64748b",
              letterSpacing: "0.1em", textTransform: "uppercase", mb: 1.25 }}>
              What to do next
            </Typography>
            {[
              "Open the email from Proove in your inbox",
              "Sign in using your email and the temporary password provided",
              "You'll be prompted to create your own personal password on first login",
              "You now have full access to Proove clinician decision support",
            ].map((step, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: i < 3 ? 1.25 : 0 }}>
                <Box sx={{
                  width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                  bgcolor: BRAND.navy, color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 800, mt: 0.1,
                }}>
                  {i + 1}
                </Box>
                <Typography sx={{ fontSize: 14, color: isDark ? "#cbd5e1" : "#475569", lineHeight: 1.65 }}>
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography sx={{ fontSize: 13.5, color: textSub, textAlign: "center", lineHeight: 1.7, mb: 2.5 }}>
            Don't see the email? Check your spam or junk folder. Still nothing?{" "}
            <Box component="a" href="mailto:customerservice@proove.com"
              sx={{ color: isDark ? BRAND.accent : BRAND.navy, fontWeight: 700, textDecoration: "underline" }}>
              Contact us
            </Box>
          </Typography>

          <Button
            fullWidth variant="outlined"
            onClick={() => navigate("/")}
            sx={{
              borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(10,14,39,0.2)",
              color: isDark ? "#94a3b8" : "#64748b",
              borderRadius: "10px", py: 1.3, fontWeight: 600, textTransform: "none", fontSize: 14,
              "&:hover": { borderColor: BRAND.navy, color: BRAND.navy, bgcolor: "transparent" },
            }}
          >
            Go to Sign In
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
}

// ── Main register page ────────────────────────────────────────────────────────
export default function RegisterPage() {
  // eslint-disable-next-line no-unused-vars -- kept for parity with source (Login.js sets these post-auth)
  const { setUser } = useContext(UserContext);
  const navigate    = useNavigate();
  const theme       = useTheme();
  const isDark      = theme.palette.mode === "dark";

  const bg      = isDark ? "#0a0f1e" : "#f0f4ff";
  const cardBg  = isDark ? "rgba(17,24,39,0.62)" : "rgba(255,255,255,0.68)";
  const textSub = isDark ? "#94a3b8" : "#64748b";

  // Patient registration now lives entirely at /register/patient (the new
  // wizard) — this page's role selector still shows both options for
  // discoverability, but defaults to clinician since patients are meant to
  // land on the wizard directly, not this single-page form.
  const [form, setForm] = useState({
    role: "clinician", first_name: "", last_name: "", email: "",
    phone: "", date_of_birth: "", practice_name: "", npi: "",
    copay_preference: "later",
    panel_selections: [],
  });
  const [loading,      setLoading]      = useState(false);
  const [message,      setMessage]      = useState(null);
  const [paymentSent,  setPaymentSent]  = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [submittedSelectedItems, setSubmittedSelectedItems] = useState([]);

  const f = (key) => ({ value: form[key], onChange: (e) => setForm((p) => ({ ...p, [key]: e.target.value })) });

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(10,14,39,0.018)",
      fontSize: 16,
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.14)" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(10,14,39,0.3)" },
      "&.Mui-focused fieldset": { borderColor: BRAND.navy, borderWidth: "2px" },
      "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(10,14,39,0.08)" },
      "& input[type='date']": {
        colorScheme: isDark ? "dark" : "light",
        "&::-webkit-calendar-picker-indicator": { opacity: 0.5, cursor: "pointer", filter: isDark ? "invert(1)" : "none" },
      },
    },
    "& .MuiOutlinedInput-input": { py: "13px", px: "14px" },
  };

  const isPatient = form.role === "patient";

  const togglePanel = (key) => {
    setForm((p) => {
      const has = p.panel_selections.includes(key);
      return {
        ...p,
        panel_selections: has
          ? p.panel_selections.filter((k) => k !== key)
          : [...p.panel_selections, key],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    // Clinician NPI validation
    if (!isPatient && form.npi && !/^\d{10}$/.test(form.npi.trim()))
      return setMessage({ severity: "error", text: "NPI must be exactly 10 digits." });

    // Patient requires phone for SMS delivery
    if (isPatient && !form.phone.trim())
      return setMessage({ severity: "error", text: "Phone number is required so we can send your credentials and payment link via text." });

    setLoading(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name:  form.last_name.trim(),
        email:      form.email.trim().toLowerCase(),
        role:       form.role,
        phone:      form.phone.trim() || undefined,
      };

      if (isPatient) {
        payload.date_of_birth    = form.date_of_birth || undefined;
        payload.copay_preference = form.copay_preference;
        payload.panel_selections = form.panel_selections;
      } else {
        payload.practice_name = form.practice_name.trim() || undefined;
        payload.npi           = form.npi.trim() || undefined;
      }

      const res = await axios.post(`${API_BASE}/register`, payload);

      if (res.data.status === "account_created" && isPatient) {
        const items = [
          { label: "Proove Lab Test Co-pay", amount: 25 },
          ...form.panel_selections.map((key) => {
            const opt = PANEL_OPTIONS.find((o) => o.key === key);
            return { label: opt ? opt.label.replace(/^\$\d+\s—\s/, "") : key, amount: PANEL_AMOUNTS[key] || 0 };
          }),
        ];
        setSubmittedSelectedItems(items);
        setPaymentSent(true);
      } else {
        setAccountCreated(true);
      }
    } catch (err) {
      setLoading(false);
      setMessage({ severity: "error", text: err.response?.data?.detail || "Registration failed. Please try again." });
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>

      {/* Background blobs */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-10%", left: "-5%",  size: 500, color: isDark ? "rgba(10,14,39,0.18)" : "rgba(10,14,39,0.07)", delay: 0 },
          { top: "60%",  right: "-8%", size: 400, color: isDark ? "rgba(99,124,219,0.08)" : "rgba(99,124,219,0.12)", delay: 1.5 },
        ].map((b, i) => (
          <motion.div key={i} style={{ position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size, borderRadius: "50%", background: b.color, filter: "blur(80px)" }}
            animate={{ scale: [1, 1.12, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 8 + i * 2, repeat: Infinity, delay: b.delay, ease: "easeInOut" }} />
        ))}
      </Box>

      {/* Header */}
      <motion.div initial={{ y: -64, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }}
        style={{ position: "relative", zIndex: 10 }}>
        <Box sx={{ bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(10,14,39,0.97)",
          backdropFilter: "blur(16px)", borderBottom: "2px solid rgba(99,124,219,0.3)" }}>
          <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, height: 64,
            display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconButton component={Link} to="/"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" } }}>
                <ArrowBack sx={{ fontSize: 20 }} />
              </IconButton>
              <Box component={Link} to="/"
                sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                <img src="/logo-light.png" alt="Proove" style={{ height: 20, width: "auto" }} />
              </Box>
            </Box>
            <ThemeToggle />
          </Box>
        </Box>
      </motion.div>

      {/* Main */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        px: 3, py: 6, position: "relative", zIndex: 10 }}>
        <motion.div initial={{ y: 30, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.15, ease: [0.34, 1.56, 0.64, 1] }}>

          <AnimatePresence mode="wait">
            {paymentSent ? (
              <PaymentSentScreen
                key="payment-sent"
                email={form.email.trim().toLowerCase()}
                phone={form.phone.trim()}
                isDark={isDark}
                selectedItems={submittedSelectedItems}
              />
            ) : accountCreated ? (
              <ClinicianAccountCreatedScreen
                key="account-created"
                email={form.email.trim().toLowerCase()}
                isDark={isDark}
              />
            ) : (
              <motion.div key="register-form" exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.2 }}>
                <Box sx={{ width: { xs: "100%", sm: 480 }, maxWidth: 480, borderRadius: 3, overflow: "hidden",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(10,14,39,0.12)"}`,
                  boxShadow: isDark
                    ? "0 24px 80px rgba(0,0,0,0.5), 0 0 50px -18px rgba(99,124,219,0.45)"
                    : "0 24px 80px rgba(10,14,39,0.12), 0 0 40px -20px rgba(99,124,219,0.3)",
                  bgcolor: cardBg,
                  backdropFilter: "blur(20px) saturate(140%)",
                  WebkitBackdropFilter: "blur(20px) saturate(140%)" }}>

                  <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.accent})` }} />

                  {/* Card header */}
                  <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3, pb: 3.5, position: "relative", overflow: "hidden" }}>
                    <Box sx={{ position: "absolute", top: -40, right: -40, width: 140, height: 140,
                      borderRadius: "50%", border: "1px solid rgba(99,124,219,0.15)" }} />
                    <Box sx={{ position: "absolute", top: -20, right: -20, width: 90, height: 90,
                      borderRadius: "50%", border: "1px solid rgba(99,124,219,0.2)" }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.accent, mb: 1 }}>
                      CREATE ACCOUNT
                    </Typography>
                    <Typography sx={{ fontFamily: "'Michroma', sans-serif", fontSize: 20, fontWeight: 400, color: "#fff", lineHeight: 1.3 }}>
                      Join Proove
                    </Typography>
                    <Typography sx={{ fontSize: 15, color: "rgba(255,255,255,0.55)", mt: 0.5 }}>
                      {isPatient
                        ? "Lab Test Copay & Activation Fee ($25) — no subscriptions"
                        : "AI-Powered Decision Support Platform"}
                    </Typography>
                  </Box>

                  {/* Form */}
                  <Box sx={{ px: 4, py: 4 }} component="form" onSubmit={handleSubmit}>

                    <AnimatePresence mode="wait">
                      {message && (
                        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                          <Alert severity={message.severity} onClose={() => setMessage(null)}
                            sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>
                            {message.text}
                          </Alert>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Role selector */}
                    <RoleSelector
                      role={form.role}
                      onChange={(r) => (r === "patient" ? navigate("/register/patient") : setForm((p) => ({ ...p, role: r })))}
                      isDark={isDark}
                    />

                    {/* Name row */}
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Field label="First name" required isDark={isDark}>
                        <TextField hiddenLabel fullWidth required placeholder="Jane" sx={inputSx} {...f("first_name")} />
                      </Field>
                      <Field label="Last name" required isDark={isDark}>
                        <TextField hiddenLabel fullWidth required placeholder="Doe" sx={inputSx} {...f("last_name")} />
                      </Field>
                    </Box>

                    {/* Role-specific fields */}
                    <AnimatePresence mode="wait">
                      {isPatient ? (
                        <motion.div key="patient-fields"
                          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}>
                          <Field label="Date of birth" required isDark={isDark}>
                            <TextField hiddenLabel type="date" fullWidth required sx={inputSx}
                              slotProps={{ htmlInput: { max: new Date().toISOString().split("T")[0] } }}
                              {...f("date_of_birth")} />
                          </Field>
                        </motion.div>
                      ) : (
                        <motion.div key="clinician-fields"
                          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}>
                          <Field label="Practice / Organization" required isDark={isDark}>
                            <TextField hiddenLabel fullWidth required placeholder="Proove Health Systems"
                              sx={inputSx} {...f("practice_name")} />
                          </Field>
                          <Field label="NPI number" required isDark={isDark} hint="10 digits">
                            <TextField hiddenLabel fullWidth required placeholder="1234567890"
                              slotProps={{ htmlInput: { maxLength: 10 } }} sx={inputSx} {...f("npi")} />
                          </Field>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <Field label="Email address" required isDark={isDark}>
                      <TextField hiddenLabel type="email" fullWidth required
                        placeholder="you@proove.com" sx={inputSx} {...f("email")} />
                    </Field>

                    <Field label="Phone number" required={isPatient} hint={isPatient ? "Required — credentials & payment link sent by text" : "Optional"} isDark={isDark}>
                      <TextField hiddenLabel type="tel" fullWidth required={isPatient}
                        placeholder="+1 555 123 4567" sx={inputSx} {...f("phone")} />
                    </Field>

                    {/* Clinician: auto-password note */}
                    <AnimatePresence>
                      {!isPatient && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}>
                          <Box sx={{
                            display: "flex", alignItems: "flex-start", gap: 1.25,
                            bgcolor: isDark ? "rgba(10,14,39,0.15)" : "rgba(10,14,39,0.05)",
                            border: `1px solid ${isDark ? "rgba(10,14,39,0.3)" : "rgba(10,14,39,0.12)"}`,
                            borderRadius: "10px", px: 2, py: 1.5, mb: 2.5,
                          }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: BRAND.accent, flexShrink: 0, mt: 0.7,
                              "@keyframes gp": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
                              animation: "gp 2s infinite" }} />
                            <Typography sx={{ fontSize: 14, color: isDark ? "#94a3b8" : "#475569", lineHeight: 1.65 }}>
                              A temporary password will be emailed to you automatically. On your first login
                              you'll be prompted to set your own personal password.
                            </Typography>
                          </Box>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Patient: copay preference + optional cash-pay panels */}
                    <AnimatePresence>
                      {isPatient && (
                        <motion.div
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}>

                          {/* Copay preference toggle */}
                          <Box sx={{ mb: 2.5 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b", mb: 0.5 }}>
                              Would you like to pay your $25 Proove Lab Test Co-pay now or at a later time?
                            </Typography>
                            <Typography sx={{ fontSize: 12.5, color: isDark ? "#64748b" : "#94a3b8", mb: 1.25 }}>
                              A secure $25 one-time payment link will be sent to your email and phone regardless of your choice.
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                              {[
                                { value: "now",   label: "I'll pay now",   desc: "I'd like to complete my copay right after signing up" },
                                { value: "later", label: "I'll pay later", desc: "I'll complete my copay when it's convenient for me" },
                              ].map((opt) => {
                                const selected = form.copay_preference === opt.value;
                                return (
                                  <Box key={opt.value} onClick={() => setForm(p => ({ ...p, copay_preference: opt.value }))}
                                    sx={{
                                      flex: 1, cursor: "pointer", borderRadius: "10px", px: 2, py: 1.5,
                                      border: `2px solid ${selected ? BRAND.navy : (isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.14)")}`,
                                      bgcolor: selected ? (isDark ? "rgba(10,14,39,0.25)" : "rgba(10,14,39,0.06)") : "transparent",
                                      transition: "all 0.15s",
                                    }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}>
                                      <Box sx={{
                                        width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                                        border: `2px solid ${selected ? BRAND.navy : (isDark ? "rgba(255,255,255,0.3)" : "rgba(10,14,39,0.3)")}`,
                                        bgcolor: selected ? BRAND.navy : "transparent",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                      }}>
                                        {selected && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#fff" }} />}
                                      </Box>
                                      <Typography sx={{ fontSize: 14, fontWeight: 700,
                                        color: selected ? (isDark ? "#fff" : BRAND.navy) : (isDark ? "#94a3b8" : "#475569") }}>
                                        {opt.label}
                                      </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 12, color: isDark ? "#64748b" : "#94a3b8", pl: 3 }}>
                                      {opt.desc}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>

                          {/* Optional cash-pay panel checklist */}
                          <Box sx={{ mb: 2.5 }}>
                            <Typography sx={{ fontSize: 12, color: isDark ? "#64748b" : "#94a3b8", lineHeight: 1.6, mb: 1.25 }}>
                              Optional — select any additional Proove testing you'd like to pay for now.
                              You can always add these later from your account.
                            </Typography>
                            <Stack spacing={1}>
                              {PANEL_OPTIONS.map((opt) => {
                                const checked = form.panel_selections.includes(opt.key);
                                return (
                                  <Box key={opt.key} onClick={() => togglePanel(opt.key)}
                                    sx={{
                                      cursor: "pointer", borderRadius: "10px", px: 1.5, py: 1.1,
                                      border: `2px solid ${checked ? BRAND.navy : (isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.14)")}`,
                                      bgcolor: checked ? (isDark ? "rgba(10,14,39,0.25)" : "rgba(10,14,39,0.06)") : "transparent",
                                      transition: "all 0.15s",
                                      display: "flex", alignItems: "flex-start",
                                    }}>
                                    <FormControlLabel
                                      sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                      control={
                                        <Checkbox
                                          checked={checked}
                                          onChange={() => togglePanel(opt.key)}
                                          onClick={(e) => e.stopPropagation()}
                                          size="small"
                                          sx={{
                                            p: 0.5, mr: 1, mt: 0.1,
                                            color: isDark ? "rgba(255,255,255,0.3)" : "rgba(10,14,39,0.3)",
                                            "&.Mui-checked": { color: BRAND.navy },
                                          }}
                                        />
                                      }
                                      label={
                                        <Box>
                                          <Typography sx={{ fontSize: 14, fontWeight: 700,
                                            color: checked ? (isDark ? "#fff" : BRAND.navy) : (isDark ? "#94a3b8" : "#475569") }}>
                                            {opt.label}
                                          </Typography>
                                          <Typography sx={{ fontSize: 12, color: isDark ? "#64748b" : "#94a3b8", lineHeight: 1.45, mt: 0.2 }}>
                                            {opt.desc}
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Box>

                          {/* Note */}
                          <Box sx={{
                            display: "flex", alignItems: "flex-start", gap: 1.25,
                            bgcolor: isDark ? "rgba(10,14,39,0.15)" : "rgba(10,14,39,0.05)",
                            border: `1px solid ${isDark ? "rgba(10,14,39,0.3)" : "rgba(10,14,39,0.12)"}`,
                            borderRadius: "10px", px: 2, py: 1.5, mb: 2.5,
                          }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: BRAND.accent, flexShrink: 0, mt: 0.7,
                              "@keyframes gp": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
                              animation: "gp 2s infinite" }} />
                            <Typography sx={{ fontSize: 14, color: isDark ? "#94a3b8" : "#475569", lineHeight: 1.65 }}>
                              Your account and login credentials are created immediately.
                              A <strong>Proove Lab Test Co-pay ($25)</strong> payment link is always sent to your email and phone,
                              plus a payment link for anything you selected above.
                            </Typography>
                          </Box>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {isPatient && (
                      <Typography sx={{ fontSize: 12, color: isDark ? "#64748b" : "#94a3b8", lineHeight: 1.65, mb: 2, textAlign: "center" }}>
                        By creating your account, you agree to receive transactional text messages and emails from
                        Proove, including your payment link(s) and account notifications.
                        Reply <strong>STOP</strong> to any text to opt out of SMS at any time.
                        To opt out of emails, contact{" "}
                        <Box component="a" href="mailto:customerservice@proove.com"
                          sx={{ color: isDark ? BRAND.accent : BRAND.navy, fontWeight: 600 }}>
                          customerservice@proove.com
                        </Box>.
                      </Typography>
                    )}

                    <Button type="submit" fullWidth variant="contained" disabled={loading}
                      sx={{
                        mt: 0.5, mb: 2,
                        background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.accentDark} 100%)`,
                        borderRadius: "10px", py: 1.6, fontWeight: 700, textTransform: "none", fontSize: 17,
                        boxShadow: "0 4px 14px rgba(10,14,39,0.35)",
                        "&:hover": { boxShadow: "0 6px 20px rgba(10,14,39,0.45)", bgcolor: BRAND.navyDark },
                      }}>
                      {loading
                        ? <CircularProgress size={24} sx={{ color: "#fff" }} />
                        : isPatient ? "Create Account →" : "Create Account"}
                    </Button>

                    <Typography sx={{ textAlign: "center", fontSize: 15, color: textSub }}>
                      Already have an account?{" "}
                      <Box component={Link} to="/"
                        sx={{ color: BRAND.accent, textDecoration: "underline", fontWeight: 600 }}>
                        Sign in
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>

          <Typography sx={{ mt: 2, fontSize: 13, color: isDark ? "#334155" : "#9ca3af", textAlign: "center" }}>
            AI analysis does not constitute medical advice. Always consult a licensed provider.
          </Typography>
        </motion.div>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: isDark ? "rgba(2,6,15,0.9)" : BRAND.navyDark, py: 2, textAlign: "center",
        position: "relative", zIndex: 10, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <Typography sx={{ fontSize: 10.5, color: "rgba(255,255,255,0.25)", letterSpacing: "0.05em" }}>
          © {new Date().getFullYear()} PROOVE GENOMICS. ALL RIGHTS RESERVED.
        </Typography>
        <Typography sx={{ fontSize: 9.5, color: "rgba(255,255,255,0.15)", letterSpacing: "0.03em", mt: 0.5 }}>
          Powered by{" "}
          <Box component="a" href="https://labarium.com/" target="_blank" rel="noopener noreferrer"
            sx={{ color: "rgba(255,255,255,0.18)", textDecoration: "none", "&:hover": { color: "rgba(255,255,255,0.35)" } }}>
            Labarium Diagnostic Systems
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}
